import { UpcomingEvent, DJProfile } from '../../../lib/supabase';
import { useState, useRef, useEffect } from 'react';

interface UpcomingEventsProps {
  events: UpcomingEvent[];
  profile?: DJProfile | null;
}

export default function UpcomingEvents({ events, profile }: UpcomingEventsProps) {
  const sectionSubtitle = profile?.section_upcoming_subtitle || 'Próximamente';
  const sectionTitle = profile?.section_upcoming_title || 'Eventos Confirmados';
  const sectionDescription = profile?.section_upcoming_description || 'No te pierdas mis próximas presentaciones. Reserva tu lugar ahora.';

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [events]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 420;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="upcoming-events" className="py-24 lg:py-32 bg-black relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-fuchsia-500/5 to-transparent"></div>
      
      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 px-4">
          <span className="text-fuchsia-400 text-xs uppercase tracking-[0.3em] font-light">{sectionSubtitle}</span>
          <h2 className="text-4xl lg:text-5xl font-black text-white mt-4 leading-tight">
            {sectionTitle}
          </h2>
          <p className="text-gray-400 text-base mt-6 leading-relaxed">
            {sectionDescription}
          </p>
        </div>

        <div className="relative">
          {canScrollLeft && (
            <button 
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-gradient-to-r from-cyan-500 to-fuchsia-500 rounded-full flex items-center justify-center text-white hover:shadow-[0_0_30px_rgba(6,182,212,0.6)] transition-all duration-300 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
            </button>
          )}
          
          {canScrollRight && (
            <button 
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-gradient-to-r from-cyan-500 to-fuchsia-500 rounded-full flex items-center justify-center text-white hover:shadow-[0_0_30px_rgba(6,182,212,0.6)] transition-all duration-300 cursor-pointer"
            >
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
          )}

          <div 
            ref={scrollContainerRef}
            onScroll={checkScroll}
            className="flex gap-4 md:gap-8 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {events.map((event) => {
              const eventDate = new Date(event.event_date);
              const day = eventDate.getDate();
              const month = eventDate.toLocaleDateString('es-ES', { month: 'short' }).toUpperCase();
              
              return (
                <div 
                  key={event.id}
                  className="flex-shrink-0 w-[280px] md:w-[400px] bg-[#252525] rounded-2xl overflow-hidden border border-gray-700 hover:border-cyan-400 transition-all duration-300 hover:shadow-[0_0_30px_rgba(6,182,212,0.3)] group"
                >
                  <div className="p-5 md:p-8">
                    <div className="flex items-start gap-4 md:gap-6 mb-4 md:mb-6">
                      <div className="w-14 h-14 md:w-20 md:h-20 flex-shrink-0 bg-gradient-to-br from-cyan-500 to-fuchsia-500 rounded-xl md:rounded-2xl flex flex-col items-center justify-center text-white shadow-lg">
                        <span className="text-xl md:text-3xl font-black leading-none">{day}</span>
                        <span className="text-[10px] md:text-xs font-bold tracking-wider mt-0.5 md:mt-1">{month}</span>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-bold text-base md:text-xl mb-1 md:mb-2 leading-tight group-hover:text-cyan-400 transition-colors duration-300">
                          {event.title}
                        </h3>
                        <p className="text-gray-400 text-xs md:text-sm line-clamp-2 leading-relaxed">
                          {event.description}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2 md:space-y-3 mb-4 md:mb-6">
                      <div className="flex items-center gap-2 md:gap-3 text-gray-400 text-xs md:text-sm">
                        <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                          <i className="ri-map-pin-line text-sm md:text-base"></i>
                        </div>
                        <span className="truncate">{event.location}</span>
                      </div>
                      
                      <div className="flex items-center gap-2 md:gap-3 text-gray-400 text-xs md:text-sm">
                        <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                          <i className="ri-building-line text-sm md:text-base"></i>
                        </div>
                        <span className="truncate">{event.venue}</span>
                      </div>
                      
                      {event.event_time && (
                        <div className="flex items-center gap-2 md:gap-3 text-gray-400 text-xs md:text-sm">
                          <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                            <i className="ri-time-line text-sm md:text-base"></i>
                          </div>
                          <span>{event.event_time.slice(0, 5)}</span>
                        </div>
                      )}
                    </div>

                    {event.ticket_url ? (
                      <a
                        href={event.ticket_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full py-2.5 md:py-3 text-center border-2 border-cyan-400 text-cyan-400 font-bold uppercase text-xs md:text-sm tracking-wider rounded-full hover:bg-cyan-400 hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer"
                      >
                        Comprar Entradas
                      </a>
                    ) : (
                      <button 
                        onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                        className="w-full py-2.5 md:py-3 text-center border-2 border-cyan-400 text-cyan-400 font-bold uppercase text-xs md:text-sm tracking-wider rounded-full hover:bg-cyan-400 hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer"
                      >
                        Reservar
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
